import { Speech } from "@-/com.npmjs.expo-speech";
import { createAudioPlayer, type AudioPlayer, type AudioSource } from "@-/com.npmjs.expo-audio";
import { ExpoSpeechRecognitionModule } from "@-/com.npmjs.expo-speech-recognition";
import ensureError from "@-/com.npmjs.ensure-error";
import onetime from "@-/com.npmjs.onetime";
import { boundedPromise, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import type { VoiceStudyAudio } from "#voice-study-session";

/** Recognizer errors that end a capture without meaning the session is broken. */
const TRANSIENT_ERRORS = new Set(["audio-capture", "interrupted", "network", "busy"]);

/** Words the recognizer is biased toward; short commands are easily misheard without it. */
const CONTEXTUAL_STRINGS = [
  "again",
  "easy",
  "good",
  "hard",
  "help",
  "next",
  "pause",
  "previous",
  "repeat",
  "reveal",
  "skip",
  "stop",
  "tap",
];

/** A clip that never reports finishing (a source that failed to load) must not hang the turn. */
const CLIP_TIMEOUT_MS = 5000;

/** Native capture must confirm it stopped before another capture may start. */
const MICROPHONE_STOP_TIMEOUT_MS = 4000;

/** Native state reads and `Speech.stop()` answer from the audio thread within a frame or two. */
const NATIVE_AUDIO_CALL_DEADLINE_MS = 2000;

/**
 * An utterance resolves only when the TTS engine reports `onDone`/`onStopped`. An engine that goes
 * silent (audio-session interruption, a dropped callback) would otherwise stall the voice turn
 * forever. Allow a slow speaking rate — about 8 characters a second, well below the default ~15 —
 * plus the engine's start-up, then treat the utterance as finished and stop the engine.
 */
const SPEECH_MS_PER_CHARACTER = 120;
const SPEECH_START_MARGIN_MS = 5000;

export function voiceStudySpeechDeadlineMs(text: string): number {
  return SPEECH_START_MARGIN_MS + text.length * SPEECH_MS_PER_CHARACTER;
}

/**
 * The clip player keeps the audio session active: expo-audio deactivates the
 * shared AVAudioSession ~100 ms after a `keepAudioSessionActive: false` player
 * stops, which would tear down the microphone under the recognizer.
 */
const getClipPlayer = onetime((): AudioPlayer => {
  const player = createAudioPlayer(null, { keepAudioSessionActive: true });
  player.volume = 1;
  return player;
});

export function createVoiceStudyAudio(): VoiceStudyAudio {
  // Every utterance still in flight: `stopSpeaking` must release ALL of them. A single slot was
  // overwritten by the next `speak`, leaving the earlier promise to its engine callback alone.
  const speechFinishers = new Set<() => void>();
  let finishClip: (() => void) | undefined;
  let stopCapture: (() => Promise<void>) | undefined;
  let clipEpoch = 0;
  return {
    listen(onResult, onEnd, onError) {
      let stopping = false;
      let ended = false;
      const endWaiters = new Set<() => void>();
      const resolveEnd = () => {
        ended = true;
        for (const waiter of endWaiters) {
          waiter();
        }
        endWaiters.clear();
      };
      const subscriptions = [
        ExpoSpeechRecognitionModule.addListener("result", (event) => {
          if (!stopping && event.isFinal) {
            // Results are alternatives, not consecutive words.
            const text = event.results[0]?.transcript;
            if (text) {
              onResult(text);
            }
          }
        }),
        ExpoSpeechRecognitionModule.addListener("error", (event) => {
          if (stopping || event.error === "aborted" || event.error === "no-speech") {
            return;
          }
          onError(
            new Error(event.message || "Speech recognition stopped. Try resuming Voice."),
            TRANSIENT_ERRORS.has(event.error),
          );
        }),
        ExpoSpeechRecognitionModule.addListener("end", () => {
          for (const subscription of subscriptions) {
            subscription.remove();
          }
          resolveEnd();
          if (!stopping) {
            onEnd();
          }
        }),
      ];
      stopCapture = async () => {
        if (ended) {
          return;
        }
        if (!stopping) {
          stopping = true;
          ExpoSpeechRecognitionModule.abort();
        }
        // A recognizer can become inactive without delivering end (for example after an
        // interrupted audio session). Confirm native inactivity before accepting that case.
        try {
          await boundedPromise<void>(
            { deadline: MICROPHONE_STOP_TIMEOUT_MS, operation: "microphone stop confirmation" },
            (resolve) => {
              if (ended) {
                resolve();
                return;
              }
              endWaiters.add(resolve);
              return () => {
                endWaiters.delete(resolve);
              };
            },
          );
        } catch {
          const state = await withDeadline(
            { deadline: NATIVE_AUDIO_CALL_DEADLINE_MS, operation: "speech recognizer state" },
            () => ExpoSpeechRecognitionModule.getStateAsync(),
          ).catch(() => "unknown");
          if (state !== "inactive") {
            throw new Error("Microphone did not stop. Exit Voice and try again.");
          }
          resolveEnd();
        } finally {
          for (const subscription of subscriptions) {
            subscription.remove();
          }
        }
      };
      try {
        ExpoSpeechRecognitionModule.start({
          addsPunctuation: false,
          contextualStrings: CONTEXTUAL_STRINGS,
          continuous: true,
          interimResults: false,
          // Byte-identical to what expo-audio configures for `allowsRecording: true`, so the
          // recognizer's per-start category reset never fights the lock-screen carrier's session.
          iosCategory: {
            category: "playAndRecord",
            categoryOptions: ["defaultToSpeaker", "allowBluetooth"],
            mode: "default",
          },
          // Echo cancellation on the input node: the prompt plays while the mic is open.
          iosVoiceProcessingEnabled: true,
          lang: "en-US",
          maxAlternatives: 1,
          volumeChangeEventOptions: { enabled: true, intervalMillis: 100 },
        });
      } catch (error) {
        for (const subscription of subscriptions) {
          subscription.remove();
        }
        resolveEnd();
        // A start right after native teardown throws transiently; the session retries.
        onError(ensureError(error), true);
      }
    },
    playClip(source: AudioSource) {
      // A clip that never reports finishing counts as finished at the deadline.
      return boundedPromise(
        { deadline: CLIP_TIMEOUT_MS, onDeadline: "resolve", operation: "voice study clip" },
        (resolve) => {
          const player = getClipPlayer();
          const epoch = ++clipEpoch;
          const subscription = player.addListener("playbackStatusUpdate", (status) => {
            if (epoch !== clipEpoch) {
              subscription.remove();
              return;
            }
            if (status.didJustFinish) {
              resolve();
            }
          });
          const interrupt = () => {
            try {
              player.pause();
            } catch {
              // Already released.
            }
            resolve();
          };
          finishClip = interrupt;
          try {
            player.replace(source);
            player.play();
          } catch {
            // A clip that cannot start is skipped like one that never finishes: the prompt text
            // still plays. (The previous promise resolved before its reject, so this was
            // already the behavior.)
            resolve();
          }
          return () => {
            subscription.remove();
            if (finishClip === interrupt) {
              finishClip = undefined;
            }
          };
        },
      );
    },
    speak(text) {
      return boundedPromise(
        {
          deadline: voiceStudySpeechDeadlineMs(text),
          onDeadline: "resolve",
          operation: "voice study utterance",
        },
        (resolve, reject, signal) => {
          const finish = () => {
            resolve();
          };
          speechFinishers.add(finish);
          // Silent engine: end the utterance so the next prompt does not talk over it.
          signal.addEventListener(
            "abort",
            () => {
              void Speech.stop().catch(() => {});
            },
            { once: true },
          );
          Speech.speak(text, {
            language: "en-US",
            onDone: finish,
            onError: (error) => reject(ensureError(error)),
            onStopped: finish,
            // Share the recognizer's playAndRecord session instead of a separate playback one.
            useApplicationAudioSession: true,
          });
          return () => {
            speechFinishers.delete(finish);
          };
        },
      );
    },
    async stopListening() {
      await stopCapture?.();
    },
    async stopSpeaking() {
      finishClip?.();
      finishClip = undefined;
      if (speechFinishers.size === 0) {
        return;
      }
      // Each finisher removes itself from the set as it settles (deleting while iterating is safe).
      for (const finish of speechFinishers) {
        finish();
      }
      await withDeadline(
        { deadline: NATIVE_AUDIO_CALL_DEADLINE_MS, operation: "stop voice study speech" },
        () => Speech.stop(),
      );
    },
  };
}
